/** Utilities for highlighting computations (ancestry, offsets, runs, adjustments) */

import { TextRunLookup, TextRun, AstText } from '../types';
import { Annotation } from '../../../Annotation/types';

/** Parent block id for a given block from the index. */
export function getParentBlockIdFromIndex(
  textRunLookup: TextRunLookup,
  blockId: number | null | undefined,
): number | null {
  if (blockId == null) {
    return null;
  }
  const runs = textRunLookup.get(blockId);
  return runs && runs.length > 0 ? (runs[0].parentBlockId ?? null) : null;
}

/** Returns true if candidate is a descendant of ancestor in the index. */
export function isDescendantBlockInIndex(
  textRunLookup: TextRunLookup,
  candidate: number | null | undefined,
  ancestor: number | null | undefined,
): boolean {
  if (candidate == null || ancestor == null) {
    return false;
  }
  let current = getParentBlockIdFromIndex(textRunLookup, candidate);
  const safetyGuard = textRunLookup.size + 5; // 5 is an arbitraty number - we just want to avoid infinite loops
  let steps = 0;
  while (current != null && steps < safetyGuard) {
    if (current === ancestor) {
      return true;
    }
    current = getParentBlockIdFromIndex(textRunLookup, current);
    steps++;
  }
  return false;
}

/** Total non-whitespace length of prior descendant runs for this run's parent block. */
export function computePriorDescendantNonWhitespaceForRun(
  run: TextRun,
  textRunLookup: TextRunLookup,
): number {
  let total = 0;
  for (const [, runs] of textRunLookup.entries()) {
    for (const r of runs) {
      if (
        r.runId < run.runId &&
        isDescendantBlockInIndex(textRunLookup, r.blockId, run.blockId)
      ) {
        total += r.nonWsEnd - r.nonWsStart;
      }
    }
  }
  return total;
}

/** Sort annotations by their start positions. */
export function sortAnnotationsByStartPosition(
  annotations: Annotation[],
): Annotation[] {
  return [...annotations].sort((a, b) => {
    if (a.position.start.blockId !== b.position.start.blockId) {
      return a.position.start.blockId - b.position.start.blockId;
    }
    return a.position.start.offset - b.position.start.offset;
  });
}

/** Finds the character index from a non-whitespace offset in text. */
export function findIndexFromNonWhitespaceOffset(
  text: string,
  offset: number,
): number {
  if (offset <= 0) {
    return 0;
  }
  let nonWhitespaceCount = 0;
  let index = 0;
  while (index < text.length && nonWhitespaceCount < offset) {
    if (!/\s/.test(text[index])) {
      nonWhitespaceCount++;
    }
    index++;
  }
  return index;
}

/** Find the text run for a text node. */
export function getRunForTextNode(
  textNode: AstText,
  textRunLookup: TextRunLookup,
): TextRun | undefined {
  const runs = textNode.blockId ? textRunLookup.get(textNode.blockId) : null;
  return runs?.find((r) => r.runId === textNode.runId);
}

/** Compute this run's start offset within its block (non-whitespace space). */
export function computeRunStartInBlock(
  run: TextRun,
  textRunLookup: TextRunLookup,
): number {
  const priorDescForRun = computePriorDescendantNonWhitespaceForRun(
    run,
    textRunLookup,
  );
  return run.nonWsStart + priorDescForRun;
}

/** Find the anchor run for same-block annotations at a given start offset. */
export function findSameBlockAnchorRun(
  textRunLookup: TextRunLookup,
  blockId: number,
  startOffset: number,
): TextRun | undefined {
  const runs = textRunLookup.get(blockId) || [];
  return runs.find((candidate) => {
    const length = candidate.nonWsEnd - candidate.nonWsStart;
    const prior = computePriorDescendantNonWhitespaceForRun(
      candidate,
      textRunLookup,
    );
    const start = candidate.nonWsStart + prior;
    const end = start + length;
    return end > startOffset && start <= startOffset;
  });
}

/** Get the smallest runId for a block, or +Infinity if none. */
export function getFirstRunIdForBlock(
  textRunLookup: TextRunLookup,
  blockId: number,
): number {
  const runs = textRunLookup.get(blockId);
  return runs && runs.length > 0
    ? Math.min(...runs.map((r) => r.runId))
    : Number.POSITIVE_INFINITY;
}

/** Adjust a single annotation's start/end to be relative to this run. */
export function adjustAnnotationPositionForRun(
  annotation: Annotation,
  run: TextRun,
  textRunLookup: TextRunLookup,
  priorDescendantNonWs: number,
  totalNonWhitespaceChars: number,
): Annotation {
  const { start, end } = annotation.position;
  const adjusted: Annotation = {
    ...annotation,
    position: { ...annotation.position },
  };

  if (
    start.blockId < run.blockId ||
    (start.blockId === run.blockId && start.offset < run.nonWsStart)
  ) {
    adjusted.position = {
      ...adjusted.position,
      start: { blockId: run.blockId, offset: 0 },
    };
  } else if (start.blockId === run.blockId) {
    const runOffset = start.offset - (run.nonWsStart + priorDescendantNonWs);
    const clamped = Math.max(
      0,
      Math.min(runOffset, run.nonWsEnd - run.nonWsStart),
    );
    adjusted.position = {
      ...adjusted.position,
      start: { blockId: run.blockId, offset: clamped },
    };
  }

  if (
    end.blockId > run.blockId ||
    (end.blockId === run.blockId && end.offset > run.nonWsEnd)
  ) {
    adjusted.position = {
      ...adjusted.position,
      end: { blockId: run.blockId, offset: totalNonWhitespaceChars },
    };
  } else if (end.blockId === run.blockId) {
    const runOffset = end.offset - (run.nonWsStart + priorDescendantNonWs);
    const clamped = Math.max(
      0,
      Math.min(runOffset, run.nonWsEnd - run.nonWsStart),
    );
    adjusted.position = {
      ...adjusted.position,
      end: { blockId: run.blockId, offset: clamped },
    };
  } else if (run.parentBlockId !== null && end.blockId === run.parentBlockId) {
    const parentId = run.parentBlockId;
    const parentSequence: { runId: number; nonWsLen: number }[] = [];
    for (const [, runs] of textRunLookup.entries()) {
      for (const r of runs) {
        if (r.blockId === parentId || r.parentBlockId === parentId) {
          parentSequence.push({
            runId: r.runId,
            nonWsLen: r.nonWsEnd - r.nonWsStart,
          });
        }
      }
    }
    parentSequence.sort((a, b) => a.runId - b.runId);
    let parentCumulative = 0;
    for (const entry of parentSequence) {
      if (entry.runId === run.runId) {
        break;
      }
      parentCumulative += entry.nonWsLen;
    }
    const localEnd = end.offset - parentCumulative;
    const clampedLocalEnd = Math.max(
      0,
      Math.min(localEnd, totalNonWhitespaceChars),
    );
    adjusted.position = {
      ...adjusted.position,
      end: { blockId: run.blockId, offset: clampedLocalEnd },
    };
  }

  return adjusted;
}
